import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, Clock, Users, ArrowLeft, Camera, Zap, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import FashionCard from '../components/FashionCard';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'LIVE NOW', class: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-400', dot: 'bg-emerald-400 animate-pulse' },
  upcoming: { label: 'UPCOMING', class: 'border-amber-400/30 bg-amber-400/10 text-amber-400', dot: 'bg-amber-400' },
  ended: { label: 'ENDED', class: 'border-gray-500/30 bg-gray-500/10 text-gray-500', dot: 'bg-gray-500' },
};

const getTimeLabel = (status) => {
  if (status === 'active') return 'Time Left';
  if (status === 'upcoming') return 'Starts';
  return 'Status';
};

const getTimeValue = (status, countdown) => {
  if (status === 'active') return countdown;
  if (status === 'upcoming') return 'Coming soon';
  return 'Challenge ended';
};

const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

const useCountdown = (endDate) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setTimeLeft('Challenge ended'); return; }
      const days = Math.floor(diff / MS_PER_DAY);
      const hours = Math.floor((diff % MS_PER_DAY) / MS_PER_HOUR);
      const mins = Math.floor((diff % MS_PER_HOUR) / MS_PER_MINUTE);
      setTimeLeft(days > 0 ? `${days}d ${hours}h ${mins}m remaining` : `${hours}h ${mins}m remaining`);
    };
    calc();
    const timer = setInterval(calc, MS_PER_MINUTE);
    return () => clearInterval(timer);
  }, [endDate]);

  return timeLeft;
};

const ChallengeDetailPage = () => {
  const { challengeId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [looks, setLooks] = useState([]);
  const [userLooks, setUserLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitPanel, setShowSubmitPanel] = useState(false);

  const fetchChallenge = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeId}`);
      setChallenge(response.data.challenge);
      setLooks(response.data.looks);
    } catch {
      toast.error('Challenge not found');
    } finally {
      setLoading(false);
    }
  }, [challengeId]);

  const fetchUserLooks = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/users/${user.username}/looks`);
      // Only show looks not already in a challenge
      setUserLooks(response.data.filter(l => !l.challenge_id));
    } catch {
      toast.error('Failed to load your looks');
    }
  }, [user]);

  useEffect(() => { fetchChallenge(); }, [fetchChallenge]);
  useEffect(() => { if (showSubmitPanel) fetchUserLooks(); }, [showSubmitPanel, fetchUserLooks]);

  const handleSubmit = async (lookId) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/challenges/${challengeId}/submit?look_id=${lookId}`);
      toast.success('Look submitted to challenge!');
      setShowSubmitPanel(false);
      fetchChallenge();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const countdown = useCountdown(challenge?.end_date || '');

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a]">
        <div className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="skeleton h-[400px] rounded-sm mb-8" />
            <div className="skeleton h-8 w-64 rounded mb-4" />
            <div className="skeleton h-4 w-96 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-500 text-lg">Challenge not found</p>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[challenge.status] || STATUS_CONFIG.ended;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="challenge-detail-page">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={challenge.cover_image} alt={challenge.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/50 to-black/20" />
        </div>

        <div className="relative z-10 w-full px-6 md:px-12 pb-12">
          <div className="max-w-7xl mx-auto">
            <Link to="/challenges" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm" data-testid="back-to-challenges">
              <ArrowLeft className="w-4 h-4" />
              All Challenges
            </Link>

            <div className="flex items-center gap-3 mb-4">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-wider font-bold ${cfg.class}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                {cfg.label}
              </span>
              <span className="px-2.5 py-1 rounded-full border border-white/20 bg-black/40 backdrop-blur-sm text-[10px] uppercase tracking-wider text-white font-medium">
                {challenge.brand}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif italic text-white mb-4 text-glow" data-testid="challenge-title">
              {challenge.title}
            </h1>
            {challenge.setting && (
              <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-4">{challenge.setting}</p>
            )}
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed italic">
              {challenge.prompt || challenge.description}
            </p>
          </div>
        </div>
      </section>

      {/* Info Bar */}
      <section className="border-y border-white/5 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Prize */}
            <div className="flex items-start gap-3" data-testid="challenge-prize">
              <div className="w-10 h-10 rounded-full bg-amber-400/10 border border-amber-400/30 flex items-center justify-center shrink-0">
                <Trophy className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Prize</p>
                <p className="text-sm text-white">{challenge.prize_description}</p>
              </div>
            </div>

            {/* Entries */}
            <div className="flex items-start gap-3" data-testid="challenge-entries">
              <div className="w-10 h-10 rounded-full bg-sky-400/10 border border-sky-400/30 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Entries</p>
                <p className="text-sm text-white">{challenge.entries_count} submissions</p>
              </div>
            </div>

            {/* Time */}
            <div className="flex items-start gap-3" data-testid="challenge-time">
              <div className="w-10 h-10 rounded-full bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                  {getTimeLabel(challenge.status)}
                </p>
                <p className="text-sm text-white">
                  {getTimeValue(challenge.status, countdown)}
                </p>
              </div>
            </div>

            {/* Action */}
            <div className="flex items-center justify-end">
              {challenge.status === 'active' && isAuthenticated && (
                <Button
                  onClick={() => setShowSubmitPanel(!showSubmitPanel)}
                  className="gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white"
                  data-testid="submit-look-btn"
                >
                  <Camera className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Submit Look</span>
                </Button>
              )}
              {challenge.status === 'active' && !isAuthenticated && (
                <Link to="/auth">
                  <Button className="btn-luxury btn-luxury-primary gap-2">
                    Join to Participate
                  </Button>
                </Link>
              )}
              {challenge.status === 'upcoming' && (
                <span className="flex items-center gap-2 text-amber-400 text-sm">
                  <Zap className="w-4 h-4" />
                  Starting soon
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Submit Panel */}
      {showSubmitPanel && (
        <section className="border-b border-white/5 bg-[#0f0f0f] px-6 md:px-12 py-8" data-testid="submit-panel">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-lg font-serif text-white mb-4">Select a Look to Submit</h3>
            {userLooks.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {userLooks.map((look) => (
                  <button
                    key={look.id}
                    onClick={() => handleSubmit(look.id)}
                    disabled={submitting}
                    className="relative group overflow-hidden rounded-sm border border-white/10 hover:border-[#C41E3A]/50 transition-all aspect-square"
                    data-testid={`submit-look-${look.id}`}
                  >
                    <img src={look.image_url} alt={look.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-[#C41E3A]" />
                    </div>
                    <div className="absolute bottom-0 inset-x-0 bg-black/80 p-2">
                      <p className="text-[10px] text-white truncate">{look.title}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No looks available. Create a look first!</p>
                <Link to="/create">
                  <Button className="btn-luxury btn-luxury-accent">Create a Look</Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Submissions Gallery */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-3">Leaderboard</p>
              <h2 className="text-3xl md:text-4xl font-serif italic text-white">Submitted Looks</h2>
              <p className="text-gray-400 text-sm mt-2">Ranked by community engagement</p>
            </div>
          </div>

          {looks.length > 0 ? (
            <div className="bento-grid">
              {looks.map((look, index) => (
                <FashionCard key={look.id} look={look} featured={index === 0} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/5 rounded-sm bg-[#121212]">
              <Camera className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">No submissions yet</p>
              <p className="text-gray-600 text-sm">Be the first to enter this challenge!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ChallengeDetailPage;
